import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTooltip from "views/system-design/DSTooltip";
import {
  Ticket,
  TicketStatus,
  TICKET_STATUSES,
  STATUS_COLORS,
} from "../../types";
import { useTickets } from "../../context/TicketsContext";
import { canChangeStatus, useOptimisticState } from "../../utils";
import { useLocalTicketOrder } from "../../hooks/useLocalTicketOrder";
import { useIsMobile } from "../../hooks/useIsMobile";
import useUsers from "components/hooks/useUsers";
import TicketCard from "./TicketCard";

interface TicketsBoardProps {
  tickets: Ticket[];
  onSelectTicket: (ticket: Ticket) => void;
  currentUser: string;
  statusFilter: TicketStatus[];
}

type Column = {
  value: TicketStatus;
  label: string;
  tickets: Ticket[];
};

function buildColumns(
  tickets: Ticket[],
  getEffectiveOrder: (ticket: Ticket) => number,
): Column[] {
  return TICKET_STATUSES.map((statusDef) => ({
    ...statusDef,
    tickets: tickets
      .filter(
        (ticket) => ticket.status === statusDef.value && !ticket.isBacklog,
      )
      .sort(
        (first, second) => getEffectiveOrder(first) - getEffectiveOrder(second),
      ),
  }));
}

export default function TicketsBoard({
  tickets,
  onSelectTicket,
  currentUser,
  statusFilter,
}: TicketsBoardProps) {
  const { updateTicket } = useTickets();
  const { getEffectiveOrder, calculateAndSetOrder } = useLocalTicketOrder();
  const { data: allUsers } = useUsers();
  const isMobile = useIsMobile();
  const boardRef = useRef<HTMLDivElement>(null);
  const [collapsedColumns, setCollapsedColumns] = useState<Set<TicketStatus>>(
    new Set(),
  );
  const prevFilterRef = useRef(statusFilter);

  const scrollPreservingUpdate = useCallback(
    (ticketId: string, updates: Partial<Ticket>) => {
      const container = boardRef.current;
      const scrollPositions = new Map<Element, number>();
      if (container) {
        container
          .querySelectorAll("[data-rfd-droppable-id]")
          .forEach((column) => {
            scrollPositions.set(column, column.scrollTop);
          });
      }
      void updateTicket(ticketId, updates);
      requestAnimationFrame(() => {
        scrollPositions.forEach((scrollTop, column) => {
          column.scrollTop = scrollTop;
        });
      });
    },
    [updateTicket],
  );

  useEffect(() => {
    const prevFilter = prevFilterRef.current;
    prevFilterRef.current = statusFilter;

    setCollapsedColumns((prev) => {
      const next = new Set(prev);
      const allStatuses = TICKET_STATUSES.map((statusDef) => statusDef.value);

      if (statusFilter.length === 0) {
        // Filter cleared — expand only columns that were auto-collapsed by filter
        for (const status of allStatuses) {
          if (
            next.has(status) &&
            prevFilter.length > 0 &&
            !prevFilter.includes(status)
          ) {
            next.delete(status);
          }
        }
      } else {
        for (const status of allStatuses) {
          const wasFiltered =
            prevFilter.length > 0 && !prevFilter.includes(status);
          const isFiltered = !statusFilter.includes(status);

          if (isFiltered && !wasFiltered) {
            next.add(status);
          } else if (!isFiltered && wasFiltered) {
            next.delete(status);
          }
        }
      }

      return next;
    });
  }, [statusFilter]);

  const toggleCollapse = (status: TicketStatus) => {
    setCollapsedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const computedColumns = useMemo(
    () => buildColumns(tickets, getEffectiveOrder),
    [tickets, getEffectiveOrder],
  );
  const [columns, setColumns] = useOptimisticState(computedColumns);

  const handleDragEnd = (result: DropResult) => {
    const { draggableId, source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    if (source.droppableId !== destination.droppableId) {
      const ticket = tickets.find((ticket) => ticket.id === draggableId);
      if (
        ticket &&
        !canChangeStatus(ticket, destination.droppableId as TicketStatus)
      )
        return;
    }

    setColumns((prev) => {
      const next = prev.map((column) => ({
        ...column,
        tickets: [...column.tickets],
      }));
      const sourceColumn = next.find(
        (column) => column.value === source.droppableId,
      );
      const destinationColumn = next.find(
        (column) => column.value === destination.droppableId,
      );
      if (!sourceColumn || !destinationColumn) return prev;

      const [moved] = sourceColumn.tickets.splice(source.index, 1);
      destinationColumn.tickets.splice(destination.index, 0, moved);
      return next;
    });

    const destColumn = columns.find(
      (column) => column.value === destination.droppableId,
    );
    if (!destColumn) return;

    const destTickets = destColumn.tickets.filter(
      (ticket) => ticket.id !== draggableId,
    );
    const destIndex = destination.index;
    const prevTicket = destIndex > 0 ? destTickets[destIndex - 1] : null;
    const nextTicket =
      destIndex < destTickets.length ? destTickets[destIndex] : null;

    calculateAndSetOrder(draggableId, prevTicket, nextTicket);

    if (source.droppableId !== destination.droppableId) {
      updateTicket(
        draggableId,
        { status: destination.droppableId as TicketStatus },
        { silent: true },
      );
    }
  };

  if (isMobile) {
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <DSBox
          ref={boardRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            pb: 2,
            overflowY: "auto",
          }}
        >
          {columns.map((column) => {
            const isCollapsed = collapsedColumns.has(column.value);

            return (
              <DSBox key={column.value}>
                <DSBox
                  onClick={() => toggleCollapse(column.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 1,
                    cursor: "pointer",
                    borderRadius: 1,
                    bgcolor: "white.5",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <DSBox
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: STATUS_COLORS[column.value],
                      flexShrink: 0,
                    }}
                  />
                  <DSTypography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {column.label}
                  </DSTypography>
                  <DSTypography
                    variant="caption"
                    sx={{ color: "text.secondary", ml: 0.5 }}
                  >
                    {column.tickets.length}
                  </DSTypography>
                  <DSTypography
                    variant="caption"
                    sx={{ color: "text.disabled", ml: "auto" }}
                  >
                    {isCollapsed ? "+" : "−"}
                  </DSTypography>
                </DSBox>

                {!isCollapsed && (
                  <Droppable droppableId={column.value}>
                    {(provided, snapshot) => (
                      <DSBox
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          bgcolor: snapshot.isDraggingOver
                            ? "action.hover"
                            : "transparent",
                          borderRadius: 2,
                          p: 0.5,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 40,
                          transition: "background-color 0.2s",
                        }}
                      >
                        {column.tickets.map((ticket, ticketIndex) => (
                          <TicketCard
                            key={ticket.id}
                            ticket={ticket}
                            index={ticketIndex}
                            onClick={() => onSelectTicket(ticket)}
                            onQuickUpdate={(ticketId, updates) =>
                              scrollPreservingUpdate(ticketId, updates)
                            }
                            currentUser={currentUser}
                            users={allUsers ?? []}
                          />
                        ))}
                        {provided.placeholder}
                      </DSBox>
                    )}
                  </Droppable>
                )}
              </DSBox>
            );
          })}
        </DSBox>
      </DragDropContext>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <DSBox
        ref={boardRef}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 2,
          minHeight: 500,
        }}
      >
        {columns.map((column) => {
          const isCollapsed = collapsedColumns.has(column.value);

          if (isCollapsed) {
            return (
              <DSTooltip
                key={column.value}
                title={`${column.label} (${column.tickets.length})`}
                placement="right"
              >
                <DSBox
                  onClick={() => toggleCollapse(column.value)}
                  sx={{
                    width: 40,
                    minWidth: 40,
                    bgcolor: "white.5",
                    borderRadius: 2,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 2,
                    gap: 1.5,
                    transition: "background-color 0.2s",
                    "&:hover": { bgcolor: "white.8" },
                  }}
                >
                  <DSBox
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: STATUS_COLORS[column.value],
                      flexShrink: 0,
                    }}
                  />
                  <DSTypography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      writingMode: "vertical-lr",
                      textOrientation: "mixed",
                      letterSpacing: 1,
                    }}
                  >
                    {column.label}
                  </DSTypography>
                  <DSTypography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "text.disabled" }}
                  >
                    {column.tickets.length}
                  </DSTypography>
                </DSBox>
              </DSTooltip>
            );
          }

          return (
            <DSBox
              key={column.value}
              sx={{
                flex: 1,
                minWidth: 280,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DSBox
                onClick={() => toggleCollapse(column.value)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  px: 1,
                  cursor: "pointer",
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <DSBox
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: STATUS_COLORS[column.value],
                  }}
                />
                <DSTypography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {column.label}
                </DSTypography>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.secondary", ml: 0.5 }}
                >
                  {column.tickets.length}
                </DSTypography>
              </DSBox>

              <Droppable droppableId={column.value}>
                {(provided, snapshot) => (
                  <DSBox
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      flex: 1,
                      bgcolor: snapshot.isDraggingOver
                        ? "action.hover"
                        : "white.5",
                      borderRadius: 2,
                      p: 1,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 100,
                      transition: "background-color 0.2s",
                    }}
                  >
                    {column.tickets.map((ticket, ticketIndex) => (
                      <TicketCard
                        key={ticket.id}
                        ticket={ticket}
                        index={ticketIndex}
                        onClick={() => onSelectTicket(ticket)}
                        onQuickUpdate={(ticketId, updates) =>
                          scrollPreservingUpdate(ticketId, updates)
                        }
                        currentUser={currentUser}
                        users={allUsers ?? []}
                      />
                    ))}
                    {provided.placeholder}
                  </DSBox>
                )}
              </Droppable>
            </DSBox>
          );
        })}
      </DSBox>
    </DragDropContext>
  );
}
